import { Check, MagnifyingGlass } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import MarketingHeading from "../../common/MarketingHeading"
import NicerBlockquote from "../../common/NicerBlockquote"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import { FeatureCard, StatsGrid, ThreeFeaturesSection } from "../shared"
import { ResearchScopingDemo } from "./ResearchScopingDemo"
import { ScatterPlotDemo } from "./ScatterPlotDemo"

const ResearchPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={MagnifyingGlass}
          title="Research"
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/research/banner.png`}
          subtitle="A dedicated research service that delivers decision-ready onchain insights, powered by Token Terminal's full data warehouse."
          ctaButtons={
            <>
              <LinkButton
                size="large"
                variant="solid"
                href="https://calendly.com/token-terminal/intro-call"
                style={{ zIndex: 4 }}
              >
                Book a call
              </LinkButton>
              <LinkButton
                size="large"
                variant="ghost"
                href="/resources/articles/launching-token-terminal-research"
                style={{ zIndex: 4 }}
              >
                Read the announcement
              </LinkButton>
            </>
          }
          whoWeServeText="For project teams, investment firms, and institutions."
          simpleWhoWeServeText="project teams, investment firms, and institutions"
        />
      }
      productId="research"
    >
      {/* Customer Logo Strip */}
      <div className="flex flex-col gap-2 items-center pt-16 pb-32">
        <div className="text-fg-secondary text-base mb-4 lg:mb-6">Trusted by</div>
        <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap max-w-[900px]">
          {productPageLogos.research.map((logo) => {
            const heightClass =
              logo.alt === "CoinShares"
                ? "h-[20px]"
                : logo.alt === "Consensys"
                  ? "h-[42px]"
                  : "h-[28px]"
            return (
              <div key={logo.alt}>
                <img
                  src={logo.srcLight}
                  alt={logo.alt}
                  className={`${heightClass} max-w-[120px] w-auto object-contain block dark:hidden opacity-70`}
                  style={logo.alt === "Consensys" ? { filter: "invert(1)" } : undefined}
                />
                <img
                  src={logo.srcDark}
                  alt={logo.alt}
                  className={`${heightClass} max-w-[120px] w-auto object-contain hidden dark:block`}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <ResearchScopingDemo />
              </FeatureCard>
            ),
            title: "Deep expertise meets deep data",
            description:
              "Bring your research question. We define the framework, select the right onchain metrics, and deliver analysis you can act on, backed by 2+ PB of standardized data. Because we build the data and methodologies, we understand edge cases and go deeper than anyone else.",
          },
          {
            component: (
              <FeatureCard className="h-[300px] group/scatter">
                <ScatterPlotDemo />
              </FeatureCard>
            ),
            title: "Deliverables designed around your needs",
            description:
              "Every engagement produces a concrete deliverable. Dashboards, one-off deep dives, or recurring reports, built around your exact needs. We go beyond the product layer and leverage the full data warehouse, complemented by direct access to protocol teams.",
          },
          {
            component: (
              <FeatureCard className="h-[300px] p-0 group/pipeline">
                <img
                  src={`${BASE_PATH}/static/images/marketing/products/research/pipeline.svg`}
                  alt="Token Terminal data pipeline — from 100+ blockchains through decoding, standardization, and aggregation to metrics"
                  className="w-full h-full object-cover transition-all duration-500 ease-out group-hover/pipeline:scale-105 group-hover/pipeline:brightness-125 invert dark:invert-0"
                  style={{ objectPosition: "75% center" }}
                />
                <div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover/pipeline:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    boxShadow:
                      "inset 0 0 60px rgba(20, 184, 166, 0.06), 0 0 30px rgba(20, 184, 166, 0.04)",
                  }}
                />
              </FeatureCard>
            ),
            title: "Backed by the full stack",
            description:
              "Our data warehouse is the industry's most comprehensive system of record by depth, coverage, and standardization. We maintain data across 100+ blockchains, 1,200+ applications, and 7,000+ tokenized assets. This proximity to the full pipeline enables reliable, comparable, institution-grade insights.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="We've had a terrific experience working with the Token Terminal Research team. They are extremely responsive and thoughtful. The team helped us define the problems we were looking to solve and quickly spun up granular dashboards to help answer our questions. Very few groups in crypto have their level of understanding when it comes to onchain data."
          author="Kavan Canekeratne"
          authorTitle="Corp Dev at Consensys"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-kavan-consensys.png`}
        />
      </div>

      {/* Paul Frambot quote — hidden pending approval, uncomment when ready:
      <div className="max-w-[900px] mx-auto mt-8">
        <NicerBlockquote
          content="Token Terminal's research team does a strong job of capturing where real adoption is happening in onchain lending – particularly how vault curators are building strategies that generate yield by lending against RWA or tokenized asset collateral. This is the level of clarity institutions need to understand how these markets are evolving in practice."
          author="Paul Frambot"
          authorTitle="CEO at Morpho"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-paul-morpho.png`}
        />
      </div>
      */}

      {/* CTA Section — single card for service product */}
      <div className="max-w-[900px] mx-auto mt-72 mb-24">
        <div className="flex flex-col items-center gap-8">
          <MarketingHeading level={2} className="text-2xl text-center">
            Let&apos;s scope your research question
          </MarketingHeading>
          <div className="flex flex-col gap-3 w-full max-w-[500px]">
            {[
              "Dedicated research team",
              "Full access to Token Terminal's data warehouse",
              "Flexible deliverables: dashboards, reports, or ongoing support",
              "Engagements scoped to your specific needs",
            ].map((feature) => (
              <div key={feature} className="flex items-center justify-start gap-2">
                <Icon as={Check} />
                <div className="text-base text-fg-default">{feature}</div>
              </div>
            ))}
          </div>
          <LinkButton size="large" href="https://calendly.com/token-terminal/intro-call">
            Book a call
          </LinkButton>
        </div>
      </div>
    </ProductPageTemplate>
  )
}

export default ResearchPage
